/** QuickRouter — Opérations sur un projet, communes à l'éditeur et aux outils de l'assistant.
 *  Chaque opération modifie `project` en place et lève une Error lisible si elle est refusée. */
import {
  newId, ComponentSchema, NetSchema, TrackSchema, ViaSchema, ZoneSchema, TextSchema, FootprintSchema,
} from './schema.js';
import { templateById } from './library/boards.js';
import { resolvePin } from './netlist.js';
import { r4 } from './geom.js';
import { guessKind } from './ops-kind.js';
import { addSymbol, forgetComponents, labelPin, syncNets } from './schematic.js';

export { guessKind };

export function findComponent(project, key) {
  return project.components.find((c) => c.id === key || c.ref === key) ?? null;
}

/** Prochain repère libre pour un préfixe (R → R1, R2…). */
export function nextRef(project, prefix) {
  const used = new Set(project.components.map((c) => c.ref));
  for (let k = 1; ; k++) if (!used.has(`${prefix}${k}`)) return `${prefix}${k}`;
}

/** Applique un gabarit de carte : contour + composants imposés (les anciens imposés partent). */
export function applyTemplate(project, templateId, params = {}, lookup = null) {
  const t = templateById(templateId);
  if (!t) throw new Error(`gabarit inconnu : ${templateId}`);
  const values = {};
  for (const p of t.params) values[p.id] = Math.min(p.max, Math.max(p.min, Number(params[p.id] ?? p.default)));
  const built = t.build(values);
  const goneComps = project.components.filter((c) => c.locked);
  const gone = new Set(goneComps.map((c) => c.ref));
  project.components = project.components.filter((c) => !c.locked);
  for (const n of project.nets) n.pins = n.pins.filter((k) => !gone.has(k.split('.')[0]));
  forgetComponents(project, goneComps.map((c) => c.id));
  project.board.template = t.id;
  project.board.outline = built.outline.map(([x, y]) => [r4(x), r4(y)]);
  if (built.mother) project.board.mother = built.mother;
  else delete project.board.mother;
  for (const c of built.components) {
    // Un repère du gabarit pris par un composant de l'utilisateur : ce dernier est renommé.
    const clash = project.components.find((x) => x.ref === c.ref);
    if (clash) renameRef(project, clash, nextRef(project, c.ref.replace(/\d+$/, '')));
    const comp = ComponentSchema.parse({ id: newId('c'), ...c });
    project.components.push(comp);
    if (lookup && project.schematic) addSymbol(project, lookup, comp);
  }
  if (lookup && project.schematic) syncNets(project, lookup);
  return { template: t.id, components: built.components.map((c) => c.ref) };
}

function renameRef(project, comp, ref) {
  const old = comp.ref;
  comp.ref = ref;
  for (const n of project.nets) n.pins = n.pins.map((k) => (k.startsWith(`${old}.`) ? `${ref}${k.slice(old.length)}` : k));
}

export function addComponent(project, lookup, spec) {
  const fp = lookup(spec.footprint);
  if (!fp) throw new Error(`empreinte inconnue : ${spec.footprint} (cherchez avec la bibliothèque)`);
  const ref = spec.ref?.trim() || nextRef(project, fp.refPrefix || 'U');
  if (project.components.some((c) => c.ref === ref)) throw new Error(`le repère ${ref} existe déjà`);
  const comp = ComponentSchema.parse({
    id: newId('c'),
    ref,
    footprint: fp.id,
    value: spec.value ?? fp.defaults?.value ?? '',
    // Les références d'achat du catalogue ne valent que pour SA valeur : une 0805 de 120 Ω ne
    // doit pas hériter de la référence LCSC de la 10 kΩ du catalogue.
    mpn: spec.mpn ?? (sameValue(spec, fp) ? fp.defaults?.mpn ?? '' : ''),
    lcsc: spec.lcsc ?? (sameValue(spec, fp) ? fp.defaults?.lcsc ?? '' : ''),
    pos: spec.pos ?? [0, 0],
    rot: spec.rot ?? 0,
    side: spec.side ?? 'top',
    dnp: spec.dnp ?? false,
    note: spec.note ?? '',
  });
  project.components.push(comp);
  // Étude d'abord : tout composant a son symbole (position libre, ou celle demandée).
  if (project.schematic) addSymbol(project, lookup, comp, spec.schPos);
  return comp;
}

const EDITABLE = ['ref', 'value', 'pos', 'rot', 'side', 'mpn', 'lcsc', 'dnp', 'note', 'footprint'];

/** La valeur demandée est-elle celle du catalogue (ou absente) ? « 10 kΩ » = « 10k ». */
function sameValue(spec, fp) {
  if (spec.value === undefined || !fp.defaults?.value) return true;
  const norm = (v) => String(v).toLowerCase().replace(/\s|Ω|ω|ohms?|f$/g, '').replace('µ', 'u');
  return norm(spec.value) === norm(fp.defaults.value);
}

export function updateComponent(project, lookup, key, patch, { force = false } = {}) {
  const comp = findComponent(project, key);
  if (!comp) throw new Error(`composant inconnu : ${key}`);
  if (comp.locked && !force && (patch.pos || patch.rot !== undefined || patch.side || patch.footprint)) {
    throw new Error(`${comp.ref} est imposé par le gabarit de carte : il ne se déplace pas`);
  }
  if (patch.footprint && !lookup(patch.footprint)) throw new Error(`empreinte inconnue : ${patch.footprint}`);
  if (patch.ref && patch.ref !== comp.ref) {
    if (project.components.some((c) => c.ref === patch.ref)) throw new Error(`le repère ${patch.ref} existe déjà`);
    renameRef(project, comp, patch.ref);
  }
  // Valeur changée : la référence d'achat héritée du catalogue ne correspond plus.
  const fp = lookup(patch.footprint ?? comp.footprint);
  if (patch.value !== undefined && patch.value !== comp.value && patch.lcsc === undefined && fp?.defaults && comp.lcsc === fp.defaults.lcsc && !sameValue({ value: patch.value }, fp)) {
    comp.lcsc = '';
    if (comp.mpn === fp.defaults.mpn) comp.mpn = '';
  }
  for (const k of EDITABLE) if (k !== 'ref' && patch[k] !== undefined) comp[k] = patch[k];
  if (patch.pos) comp.pos = patch.pos.map(r4);
  if (patch.rot !== undefined) comp.rot = ((Number(patch.rot) % 360) + 360) % 360;
  ComponentSchema.parse(comp);
  return comp;
}

export function deleteComponents(project, keys, { force = false } = {}) {
  const gone = [];
  for (const k of keys) {
    const c = findComponent(project, k);
    if (!c) continue;
    if (c.locked && !force) throw new Error(`${c.ref} est imposé par le gabarit de carte`);
    gone.push(c.ref);
  }
  const set = new Set(gone);
  forgetComponents(project, project.components.filter((c) => set.has(c.ref)).map((c) => c.id));
  project.components = project.components.filter((c) => !set.has(c.ref));
  for (const n of project.nets) n.pins = n.pins.filter((k) => !set.has(k.split('.')[0]));
  return gone;
}

/**
 * Crée ou complète un net. mode « replace » : la liste fournie devient la liste du net ;
 * « add » : ajoutée à l'existant. Une pastille n'appartient qu'à UN net (retirée des autres).
 */
export function setNet(project, lookup, { name, pins = [], kind, width, mode = 'replace' }) {
  const clean = String(name).trim();
  if (!clean) throw new Error('nom de net vide');
  const keys = [...new Set(pins.flatMap((p) => resolvePin(project, lookup, p)))];
  if (project.schematic) return setNetSchematic(project, lookup, { name: clean, keys, kind, width, mode });
  let net = project.nets.find((n) => n.name === clean);
  if (!net) {
    net = NetSchema.parse({ id: newId('n'), name: clean, pins: [], kind: kind ?? guessKind(clean) });
    project.nets.push(net);
  }
  const target = mode === 'add' ? [...new Set([...net.pins, ...keys])] : keys;
  const taken = new Set(target);
  for (const other of project.nets) if (other !== net) other.pins = other.pins.filter((k) => !taken.has(k));
  net.pins = target;
  if (kind) net.kind = kind;
  if (width !== undefined) net.width = width || undefined;
  project.nets = project.nets.filter((n) => n.pins.length || n === net);
  return net;
}

/** setNet d'un projet à schéma : les broches reçoivent une étiquette attachée, puis la
 *  netlist est recalculée (une broche n'a qu'une étiquette attachée : exclusivité conservée). */
function setNetSchematic(project, lookup, { name, keys, kind, width, mode }) {
  const byRef = new Map(project.components.map((c) => [c.ref, c]));
  if (mode !== 'add') {
    project.schematic.labels = project.schematic.labels.filter((l) => !(l.at && l.name === name));
  }
  for (const key of keys) {
    const dot = key.lastIndexOf('.');
    labelPin(project, byRef.get(key.slice(0, dot)), key.slice(dot + 1), name);
  }
  syncNets(project, lookup);
  const net = project.nets.find((n) => n.name === name) ?? { name, pins: [], kind: kind ?? guessKind(name) };
  if (kind) net.kind = kind;
  if (width !== undefined) { if (width) net.width = width; else delete net.width; }
  return net;
}

export function deleteNets(project, names, { withRoutes = true, lookup = null } = {}) {
  const set = new Set(names);
  if (project.schematic) {
    // Les étiquettes partent ; des broches restées reliées par des fils forment un net sans nom.
    project.schematic.labels = project.schematic.labels.filter((l) => !set.has(l.name));
  }
  project.nets = project.nets.filter((n) => !set.has(n.name));
  if (project.schematic && lookup) syncNets(project, lookup);
  if (withRoutes) clearRoutes(project, { nets: names });
  return names;
}

/** Retire des pistes/vias : de certains nets, seulement les automatiques, ou tout. */
export function clearRoutes(project, { nets = null, autoOnly = false } = {}) {
  const set = nets ? new Set(nets) : null;
  const hit = (x) => (!set || set.has(x.net)) && (!autoOnly || x.auto);
  const before = project.tracks.length + project.vias.length;
  project.tracks = project.tracks.filter((t) => !hit(t));
  project.vias = project.vias.filter((v) => !hit(v));
  return before - project.tracks.length - project.vias.length;
}

export function addTrack(project, spec) {
  const t = TrackSchema.parse({ id: newId('t'), ...spec, pts: spec.pts.map((p) => p.map(r4)) });
  project.tracks.push(t);
  return t;
}

export function addVia(project, spec) {
  const v = ViaSchema.parse({
    id: newId('v'),
    drill: project.rules.viaDrill,
    diameter: project.rules.viaDiameter,
    ...spec,
    pos: spec.pos.map(r4),
  });
  project.vias.push(v);
  return v;
}

export function deleteItems(project, ids) {
  const set = new Set(ids);
  const n0 = project.tracks.length + project.vias.length + project.texts.length + project.zones.length;
  project.tracks = project.tracks.filter((t) => !set.has(t.id));
  project.vias = project.vias.filter((v) => !set.has(v.id));
  project.texts = project.texts.filter((t) => !set.has(t.id));
  project.zones = project.zones.filter((z) => !set.has(z.id));
  return n0 - (project.tracks.length + project.vias.length + project.texts.length + project.zones.length);
}

export function upsertZone(project, spec) {
  const existing = project.zones.find((z) => (spec.id && z.id === spec.id) || (!spec.id && z.net === spec.net && z.layer === spec.layer));
  const z = ZoneSchema.parse({ id: existing?.id ?? newId('z'), ...existing, ...spec });
  project.zones = project.zones.filter((x) => x !== existing);
  project.zones.push(z);
  return z;
}

export function addText(project, spec) {
  const t = TextSchema.parse({ id: newId('s'), ...spec });
  project.texts.push(t);
  return t;
}

export function upsertFootprint(project, def) {
  const f = FootprintSchema.parse(def);
  project.footprints = project.footprints.filter((x) => x.id !== f.id);
  project.footprints.push(f);
  return f;
}
